import { useMutation } from '@tanstack/react-query';
import { uploadFile, type ChatAttachment } from '@/services/DataService';
import { ANALYTICS_EVENTS, useTrack } from '@/lib/analytics';

/**
 * Imperatively upload a single file to the tenant file store (`POST /file`).
 *
 * Returns a referentially-stable `(file: File) => Promise<ChatAttachment>` to
 * inject into the composer's `PromptInput`. The composer fires one call per
 * picked file and tracks per-file upload status itself, so we expose the raw
 * `mutateAsync` rather than the (single) mutation pending state.
 */
export const useUploadFile = (): ((file: File) => Promise<ChatAttachment>) => {
  const trackEvent = useTrack();
  const { mutateAsync } = useMutation({
    mutationFn: (file: File) => uploadFile(file),
    onSuccess: (_result, file) => {
      trackEvent(ANALYTICS_EVENTS.CHAT_FILE_ATTACHED, {
        file_type: file.type || 'unknown',
        file_size_bytes: file.size,
      });
    },
  });
  return mutateAsync;
};
